import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

const brandCardVariants = cva(
	"rounded-lg border border-border transition-colors duration-150",
	{
		variants: {
			variant: {
				default: "bg-card",
				elevated: "bg-card shadow-sm",
				blush: "bg-accent border-primary/10",
			},
			padding: {
				none: "",
				sm: "p-4",
				default: "p-5",
				lg: "p-6",
			},
		},
		defaultVariants: {
			variant: "default",
			padding: "default",
		},
	},
);

export interface BrandCardProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof brandCardVariants> {}

const BrandCard = React.forwardRef<HTMLDivElement, BrandCardProps>(
	({ className, variant, padding, ...props }, ref) => {
		return (
			<div
				ref={ref}
				className={cn(brandCardVariants({ variant, padding, className }))}
				{...props}
			/>
		);
	},
);
BrandCard.displayName = "BrandCard";

const BrandCardHeader = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn("flex flex-col space-y-1", className)}
		{...props}
	/>
));
BrandCardHeader.displayName = "BrandCardHeader";

const BrandCardTitle = React.forwardRef<
	HTMLHeadingElement,
	React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
	<h3
		ref={ref}
		className={cn(
			"text-lg font-semibold text-foreground leading-none tracking-tight",
			className,
		)}
		{...props}
	/>
));
BrandCardTitle.displayName = "BrandCardTitle";

const BrandCardDescription = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
	<p
		ref={ref}
		className={cn("text-base leading-relaxed text-muted-foreground", className)}
		{...props}
	/>
));
BrandCardDescription.displayName = "BrandCardDescription";

const BrandCardContent = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
	<div ref={ref} className={cn("", className)} {...props} />
));
BrandCardContent.displayName = "BrandCardContent";

export {
	BrandCard,
	BrandCardHeader,
	BrandCardTitle,
	BrandCardDescription,
	BrandCardContent,
	brandCardVariants,
};
